import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { QuotaNoticeService } from 'app/core/services/quota-notice.service';
import { NERA_SUPPRESS_ERROR_TOAST } from 'app/core/tokens';
import { narrowQuotaExceededError, parseQuotaError } from 'app/shared/utils/quota.utils';
import { catchError, tap } from 'rxjs';

/**
 * Turns the quota middleware's machine-readable verdict into something a user
 * can act on: reads the 429 body's usage/limit details (otherwise dropped by
 * the shared error interceptor's generic "too many requests" toast), and
 * surfaces `X-Quota-Degraded` (fail-open, unmetered) as a banner on success.
 *
 * MUST be registered AFTER `neraApiErrorInterceptor` — Angular runs
 * interceptors outside-in going down and inside-out coming back, so a later
 * entry sees the error FIRST, the only position from which its toast can be
 * pre-empted. Does NOT swallow the error into `EMPTY` (the caller still needs
 * its own error handler to run); instead opts out of the toast via
 * `NERA_SUPPRESS_ERROR_TOAST` before re-throwing.
 *
 * Also narrows the envelope `QUOTA_EXCEEDED` code to the resource that ran out
 * (`QUOTA_EXCEEDED_API_REQUESTS`, `QUOTA_EXCEEDED_MEMBERS`), because the code
 * alone does not say which limit was hit (kb#5797).
 */
export const quotaInterceptor: HttpInterceptorFn = (req, next) => {
    const notices = inject(QuotaNoticeService);

    return next(req).pipe(
        tap((event) => {
            if (event instanceof HttpResponse) {
                notices.observeHeaders(event.headers);
            }
        }),
        catchError((rawError: unknown) => {
            // Names the exhausted resource in the envelope code so the shared
            // interceptor above toasts the right limit, not a generic member one.
            const error = narrowQuotaExceededError(rawError, req.method, req.url);
            const quota = parseQuotaError(error);
            if (!quota) {
                // A plain 429 really is a rate limit, and a plain 503 really is an
                // outage. Both keep their generic message and toast.
                throw error;
            }

            notices.reportDenial(quota);
            // Suppress the misleading generic copy; the banner tells the true story.
            req.context.set(NERA_SUPPRESS_ERROR_TOAST, true);
            throw error;
        })
    );
};
